import React, { useEffect, useMemo } from 'react';
import { useReportes } from './ReportesContex';
import ReactApexChart from 'react-apexcharts';
import '../../styles/reportes.css';

// ─── Color utilities ──────────────────────────────────────────────────────────

function getPrimaryColor() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--color').trim();
    return raw.startsWith('#') ? raw.slice(1) : raw;
}

function blendHex(hex, blendWith, ratio) {
    const parse = (h) => [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
    ];
    const [r1, g1, b1] = parse(hex.padStart(6, '0'));
    const [r2, g2, b2] = parse(blendWith.padStart(6, '0'));
    const r = Math.round(r1 * ratio + r2 * (1 - ratio));
    const g = Math.round(g1 * ratio + g2 * (1 - ratio));
    const b = Math.round(b1 * ratio + b2 * (1 - ratio));
    return [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

function toArgb(hex) {
    return `FF${hex.toUpperCase()}`;
}

// ─── Gauge helpers ─────────────────────────────────────────────────────────────

const gaugeColorClass = (val) => {
    if (val == null || isNaN(val)) return 'grey';
    if (val >= 75) return 'green';
    if (val >= 60) return 'yellow';
    return 'red';
};

const gaugeHexColor = (val) => {
    if (val == null || isNaN(val)) return '#adb5bd';
    if (val >= 75) return '#2ccb9e';
    if (val >= 60) return '#ffc800';
    return '#fe424d';
};

function fmtPct(val, decimals = 1) {
    if (val == null || isNaN(val)) return '-';
    return `${parseFloat(val).toFixed(decimals)}%`;
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function ProgressCell({ value }) {
    const cls = gaugeColorClass(value);
    const safeVal = Math.min(Math.max(value ?? 0, 0), 100);
    return (
        <div className="ro-progress-cell">
            <span className="ro-progress-value">{fmtPct(value)}</span>
            <svg className="ro-progress-svg" viewBox="0 0 100 6" preserveAspectRatio="none">
                <rect x="0" y="0" width="100" height="6" rx="3" ry="3" className="ro-progress-svg-track" />
                <rect
                    x="0" y="0"
                    width="100"
                    height="6"
                    rx="3" ry="3"
                    className={`ro-progress-svg-fill ro-progress-svg-fill--${cls}`}
                    style={{ '--bar-scale': safeVal / 100 }}
                />
            </svg>
        </div>
    );
}

function GaugeCard({ label, value }) {
    const color = gaugeHexColor(value);
    const safeVal = value != null && !isNaN(value) ? parseFloat(parseFloat(value).toFixed(1)) : 0;

    const options = useMemo(() => ({
        chart: {
            type: 'radialBar', sparkline: { enabled: true }, parentHeightOffset: 0,
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
                animateGradually: { enabled: true, delay: 150 },
                dynamicAnimation: { enabled: true, speed: 500 },
            },
        },
        plotOptions: {
            radialBar: {
                startAngle: -135,
                endAngle: 135,
                hollow: { size: '60%' },
                track: { background: '#e8ecf1', strokeWidth: '100%', margin: 2 },
                dataLabels: {
                    name: { show: false },
                    value: {
                        offsetY: 8,
                        fontSize: '18px',
                        fontFamily: 'WorkSans',
                        fontWeight: 700,
                        color: '#13151c',
                        formatter: (v) => `${v}%`,
                    },
                },
            },
        },
        fill: { colors: [color] },
        stroke: { lineCap: 'round' },
    }), [color]);

    return (
        <div className="ro-gauge-card">
            <ReactApexChart type="radialBar" series={[safeVal]} options={options} height={160} />
            <span className="ro-gauge-label">{label}</span>
        </div>
    );
}

// ─── Column definitions (shared for UI + Excel) ───────────────────────────────

const COLS_PROD = [
    { key: 'piezasPlaneadas',     label: 'Pzas. Plan.',  format: 'loc' },
    { key: 'piezasReal',          label: 'Pzas. Real',   format: 'loc' },
    { key: 'pctPiezas',           label: 'Pzas. %',      format: 'pct' },
    { key: 'ordenesPlaneadas',    label: 'Corr. Plan.',  format: 'num' },
    { key: 'ordenesReal',         label: 'Corr. Real',   format: 'num' },
    { key: 'cumplimientoOrdenes', label: 'Cumpl. %',     format: 'pct' },
];

const COLS_REND = [
    { key: 'disponibilidad', label: 'Disponibilidad', format: 'progress' },
    { key: 'desempeno',      label: 'Desempeño',      format: 'progress' },
    { key: 'calidad',        label: 'Calidad',         format: 'progress' },
    { key: 'oee',            label: 'OEE',             format: 'progress' },
];

const COLS_LLAMADAS = [
    { key: 'antesAbiertas',   label: 'Antes — Abiertas',   format: 'num' },
    { key: 'antesCerradas',   label: 'Antes — Cerradas',   format: 'num' },
    { key: 'periodoAbiertas', label: 'Periodo — Abiertas', format: 'num' },
    { key: 'periodoCerradas', label: 'Periodo — Cerradas', format: 'num' },
    { key: 'numParos',        label: '# Paros',            format: 'num' },
    { key: 'pctCierre',       label: '% Cierre',           format: 'pct' },
    { key: 'tRespuesta',      label: 'T. Respuesta',       format: 'str' },
    { key: 'tReparacion',     label: 'T. Reparación',      format: 'str' },
];

// All columns for Excel export
const COLUMNS    = [...COLS_PROD, ...COLS_REND, ...COLS_LLAMADAS];
const COL_GROUPS = [
    { label: 'Producción',   count: COLS_PROD.length },
    { label: 'Rendimiento',  count: COLS_REND.length },
    { label: 'Llamadas',     count: COLS_LLAMADAS.length },
];

function renderCell(col, val) {
    if (col.format === 'progress') return <ProgressCell value={val} />;
    if (col.format === 'pct')  return fmtPct(val);
    if (col.format === 'loc')  return val != null ? val.toLocaleString() : '-';
    if (col.format === 'num')  return val != null ? val : '-';
    return val != null ? val : '-';
}

// ─── Metric derivation ─────────────────────────────────────────────────────────

const safeN = (v) => (v != null && !isNaN(Number(v)) ? Number(v) : 0);

function deriveRow(item) {
    const tDisp     = safeN(item.tiempoDisponible);
    const tOper     = safeN(item.tiempoOperando);
    const pProd     = safeN(item.piezasProd);
    const pMalas    = safeN(item.piezasMalas);
    const pIdeales  = safeN(item.piezasIdealesCalc);
    const meta      = safeN(item.meta);
    const corridas  = safeN(item.corridas);
    const corridasT = safeN(item.numCorridasTrabajadas);
    const n1        = safeN(item.dtGenAntesCerrAhora);
    const n2        = safeN(item.dtGenAhoraCerrAhora);
    // eslint-disable-next-line no-underscore-dangle
    const r1   = safeN(item.dtGenAntesCerrAhora_TRespuesta);
    // eslint-disable-next-line no-underscore-dangle
    const r2   = safeN(item.dtGenAhoraCerrAhora_TRespuesta);
    // eslint-disable-next-line no-underscore-dangle
    const rep1 = safeN(item.dtGenAntesCerrAhora_TReparacion);
    // eslint-disable-next-line no-underscore-dangle
    const rep2 = safeN(item.dtGenAhoraCerrAhora_TReparacion);
    const antes = safeN(item.dtGenAntes);
    const ahora = safeN(item.dtGenAhora);

    const disponibilidad = tDisp    > 0 ? (tOper   / tDisp)    * 100 : null;
    const desempeno      = pIdeales > 0 ? (pProd   / pIdeales) * 100 : null;
    const calidad        = pProd    > 0 ? ((pProd - pMalas) / pProd) * 100 : null;
    const oee            = (disponibilidad != null && desempeno != null && calidad != null)
        ? (disponibilidad / 100) * (desempeno / 100) * (calidad / 100) * 100
        : null;

    const cumplimientoOrdenes = corridas > 0 ? (corridasT / corridas) * 100 : null;
    const pctPiezas           = meta     > 0 ? (pProd / meta) * 100 : null;

    const totalCerradas  = n1 + n2;
    const totalGeneradas = antes + ahora;
    const pctCierre      = totalGeneradas > 0 ? (totalCerradas / totalGeneradas) * 100 : null;
    const tRespRaw       = totalCerradas > 0 ? (n1 * r1 + n2 * r2) / totalCerradas : null;
    const tRepRaw        = totalCerradas > 0 ? (n1 * rep1 + n2 * rep2) / totalCerradas : null;

    return {
        turno:                item.turno,
        piezasPlaneadas:      meta       > 0 ? meta       : null,
        piezasReal:           pProd      > 0 ? pProd      : null,
        pctPiezas,
        ordenesPlaneadas:     corridas   > 0 ? corridas   : null,
        ordenesReal:          corridasT  > 0 ? corridasT  : null,
        cumplimientoOrdenes,
        disponibilidad,
        desempeno,
        calidad,
        oee,
        antesAbiertas:        item.dtGenAntesAbierta ?? null,
        antesCerradas:        n1 > 0 ? n1 : null,
        periodoAbiertas:      item.dtGenAhoraAbierta ?? null,
        periodoCerradas:      n2 > 0 ? n2 : null,
        numParos:             item.numParos ?? null,
        pctCierre,
        tRespuesta:           tRespRaw != null ? `${tRespRaw.toFixed(1)} min` : '-',
        tReparacion:          tRepRaw  != null ? `${tRepRaw.toFixed(1)} min`  : '-',
    };
}

function aggregateRows(rawData) {
    const sumF = (field) => rawData.reduce((a, item) => a + safeN(item[field]), 0);

    const tTDisp    = sumF('tiempoDisponible');
    const tTOper    = sumF('tiempoOperando');
    const tTFalla   = sumF('tiempoEnFalla');
    const tPProd    = sumF('piezasProd');
    const tPMalas   = sumF('piezasMalas');
    const tPIdeales = sumF('piezasIdealesCalc');
    const tMeta     = sumF('meta');
    const tCorridas = sumF('corridas');
    const tCorridasT = sumF('numCorridasTrabajadas');
    const tParos    = sumF('numParos');
    const tN1       = sumF('dtGenAntesCerrAhora');
    const tN2       = sumF('dtGenAhoraCerrAhora');
    const tAntes    = sumF('dtGenAntes');
    const tAhora    = sumF('dtGenAhora');
    const tCerradas  = tN1 + tN2;
    const tGeneradas = tAntes + tAhora;

    const disponibilidad = tTDisp    > 0 ? (tTOper   / tTDisp)    * 100 : null;
    const desempeno      = tPIdeales > 0 ? (tPProd   / tPIdeales) * 100 : null;
    const calidad        = tPProd    > 0 ? ((tPProd - tPMalas) / tPProd) * 100 : null;
    const oee            = (disponibilidad != null && desempeno != null && calidad != null)
        ? (disponibilidad / 100) * (desempeno / 100) * (calidad / 100) * 100 : null;

    const wTRespSum = rawData.reduce((acc, item) => {
        const n1 = safeN(item.dtGenAntesCerrAhora), n2 = safeN(item.dtGenAhoraCerrAhora);
        // eslint-disable-next-line no-underscore-dangle
        return acc + n1 * safeN(item.dtGenAntesCerrAhora_TRespuesta) + n2 * safeN(item.dtGenAhoraCerrAhora_TRespuesta);
    }, 0);
    const wTRepSum = rawData.reduce((acc, item) => {
        const n1 = safeN(item.dtGenAntesCerrAhora), n2 = safeN(item.dtGenAhoraCerrAhora);
        // eslint-disable-next-line no-underscore-dangle
        return acc + n1 * safeN(item.dtGenAntesCerrAhora_TReparacion) + n2 * safeN(item.dtGenAhoraCerrAhora_TReparacion);
    }, 0);

    const tRespRaw = tCerradas > 0 ? wTRespSum / tCerradas : null;
    const tRepRaw  = tCerradas > 0 ? wTRepSum  / tCerradas : null;
    const mtbf     = tParos    > 0 ? tTDisp    / tParos    : null;
    const mttr     = tParos    > 0 ? tTFalla   / tParos    : null;

    return {
        totals: {
            isTotal: true,
            piezasPlaneadas:     tMeta      > 0 ? tMeta       : null,
            piezasReal:          tPProd     > 0 ? tPProd      : null,
            pctPiezas:           tMeta      > 0 ? (tPProd / tMeta) * 100 : null,
            ordenesPlaneadas:    tCorridas  > 0 ? tCorridas   : null,
            ordenesReal:         tCorridasT > 0 ? tCorridasT  : null,
            cumplimientoOrdenes: tCorridas  > 0 ? (tCorridasT / tCorridas) * 100 : null,
            disponibilidad,
            desempeno,
            calidad,
            oee,
            antesAbiertas:   sumF('dtGenAntesAbierta') || null,
            antesCerradas:   tN1  > 0 ? tN1  : null,
            periodoAbiertas: sumF('dtGenAhoraAbierta') || null,
            periodoCerradas: tN2  > 0 ? tN2  : null,
            numParos:        tParos > 0 ? tParos : null,
            pctCierre:       tGeneradas > 0 ? (tCerradas / tGeneradas) * 100 : null,
            tRespuesta:      tRespRaw != null ? `${tRespRaw.toFixed(1)} min` : '-',
            tReparacion:     tRepRaw  != null ? `${tRepRaw.toFixed(1)} min`  : '-',
        },
        mtbf,
        mttr,
    };
}

// ─── Excel export ──────────────────────────────────────────────────────────────

async function exportarExcel(allRows, selectedArea, selectedLinea, fechaNominal, mtbf, mttr) {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');

    const primary   = getPrimaryColor();
    const headerBg  = blendHex(primary, '000000', 0.8);
    const subHdrBg  = blendHex(primary, 'ffffff', 0.3);
    const infoBg    = blendHex(primary, 'ffffff', 0.85);
    const totalBg   = blendHex(primary, 'ffffff', 0.1);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Reporte de Operación');

    const totalCols = COLUMNS.length + 1;
    const lastColLetter = String.fromCharCode(64 + totalCols);

    ws.mergeCells(`A1:${lastColLetter}1`);
    ws.getCell('A1').value = 'Reporte de Operación';
    ws.getCell('A1').style = {
        font: { bold: true, size: 14, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(headerBg) } },
        alignment: { horizontal: 'center', vertical: 'middle' },
    };
    ws.getRow(1).height = 28;

    const infoText = [
        selectedArea?.nombre  ? `Área: ${selectedArea.nombre}`   : '',
        selectedLinea?.nombre ? `Línea: ${selectedLinea.nombre}` : '',
        fechaNominal          ? `Fecha: ${String(fechaNominal).slice(0, 10)}` : '',
    ].filter(Boolean).join('   |   ');
    ws.mergeCells(`A2:${lastColLetter}2`);
    ws.getCell('A2').value = infoText;
    ws.getCell('A2').style = {
        font: { size: 11 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(infoBg) } },
        alignment: { horizontal: 'left', vertical: 'middle' },
    };
    ws.getRow(2).height = 18;

    const baseStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(headerBg) } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: { bottom: { style: 'thin', color: { argb: `FF${primary.toUpperCase()}` } } },
    };
    ws.getCell('A3').value = 'Turno';
    ws.getCell('A3').style = baseStyle;
    ws.mergeCells('A3:A4');

    let colIdx = 2;
    COL_GROUPS.forEach((grp) => {
        ws.getCell(3, colIdx).value = grp.label;
        ws.getCell(3, colIdx).style = baseStyle;
        if (grp.count > 1) ws.mergeCells(3, colIdx, 3, colIdx + grp.count - 1);
        colIdx += grp.count;
    });
    ws.getRow(3).height = 20;

    COLUMNS.forEach((col, i) => {
        const cell = ws.getCell(4, i + 2);
        cell.value = col.label;
        cell.style = {
            font: { bold: true, size: 10, color: { argb: 'FFFFFFFF' } },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(subHdrBg) } },
            alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        };
    });
    ws.getRow(4).height = 32;

    allRows.forEach((row, i) => {
        const rowNum = i + 5;
        const isTotal = row.isTotal;
        const label = isTotal ? 'Total' : `Turno ${row.turno}`;
        const fillArgb = isTotal ? toArgb(totalBg) : (i % 2 === 0 ? 'FFFFFFFF' : 'FFF7F8FE');

        ws.getCell(rowNum, 1).value = label;
        COLUMNS.forEach((col, j) => {
            const val = row[col.key];
            const cell = ws.getCell(rowNum, j + 2);
            if (col.format === 'pct' || col.format === 'progress') {
                cell.value = val != null && !isNaN(val) ? parseFloat(parseFloat(val).toFixed(1)) : null;
                cell.numFmt = '0.0"%"';
            } else if (col.format === 'loc' || col.format === 'num') {
                cell.value = val != null ? val : null;
                if (col.format === 'loc') cell.numFmt = '#,##0';
            } else {
                cell.value = val != null ? String(val) : null;
            }
        });
        for (let c = 1; c <= totalCols; c++) {
            const cell = ws.getCell(rowNum, c);
            cell.style = {
                ...cell.style,
                font: { ...(cell.style.font || {}), bold: isTotal, size: 10 },
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } },
                alignment: { horizontal: 'center', vertical: 'middle' },
            };
        }
        ws.getRow(rowNum).height = 18;
    });

    if (mtbf != null || mttr != null) {
        const footerRow = allRows.length + 6;
        ws.getCell(footerRow, 1).value = `MTBF: ${mtbf != null ? `${parseFloat(mtbf).toFixed(1)} min` : '-'}   |   MTTR: ${mttr != null ? `${parseFloat(mttr).toFixed(1)} min` : '-'}`;
        ws.mergeCells(footerRow, 1, footerRow, totalCols);
        ws.getCell(footerRow, 1).style = {
            font: { bold: true, size: 11 },
            alignment: { horizontal: 'left', vertical: 'middle' },
        };
        ws.getRow(footerRow).height = 20;
    }

    ws.getColumn(1).width = 12;
    COLUMNS.forEach((_, i) => { ws.getColumn(i + 2).width = 13; });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `reporte_operacion_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ─── Table helpers ─────────────────────────────────────────────────────────────

function SectionTable({ cols, rows, sectionLabel }) {
    return (
        <div className="ro-section-block">
            <p className="ro-section-label">{sectionLabel}</p>
            <div className="ro-table-card">
                <table className="ro-table">
                    <thead>
                        <tr>
                            <th className="ro-th-group ro-col-turno">Turno</th>
                            {cols.map((col) => (
                                <th key={col.key} className="ro-th-sub">{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => {
                            const isTotal = row.isTotal;
                            const label   = isTotal ? 'Total' : `T${row.turno}`;
                            const rowCls  = isTotal ? 'ro-row ro-row-total' : 'ro-row';
                            const tdCls   = isTotal ? 'ro-td ro-td-total'   : 'ro-td';
                            return (
                                <tr key={i} className={rowCls}>
                                    <td className={`${tdCls} ro-td-label`}>{label}</td>
                                    {cols.map((col) => (
                                        <td
                                            key={col.key}
                                            className={`${tdCls}${col.format === 'progress' ? ' ro-td-progress' : ''}`}
                                        >
                                            {renderCell(col, row[col.key])}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function ReporteOperacion() {
    const {
        reporteOperacionData,
        isLoadingLlamadas,
        selectedArea,
        selectedLinea,
        setExportFn,
    } = useReportes();

    const rawData = reporteOperacionData;

    const { allRows, gaugeSource, mtbf, mttr, fechaNominal } = useMemo(() => {
        if (!Array.isArray(rawData) || rawData.length === 0) {
            return { allRows: [], gaugeSource: null, mtbf: null, mttr: null, fechaNominal: null };
        }

        const rows = rawData.map(deriveRow);
        const { totals, mtbf: mb, mttr: mt } = aggregateRows(rawData);

        return {
            allRows:      rawData.length > 1 ? [...rows, totals] : rows,
            gaugeSource:  totals,
            mtbf:         mb,
            mttr:         mt,
            fechaNominal: rawData[0]?.fechaNominal ?? null,
        };
    }, [rawData]);

    useEffect(() => {
        if (allRows.length > 0) {
            setExportFn(() => () => exportarExcel(allRows, selectedArea, selectedLinea, fechaNominal, mtbf, mttr));
        } else {
            setExportFn(null);
        }
        return () => setExportFn(null);
    }, [allRows, selectedArea, selectedLinea, fechaNominal, mtbf, mttr, setExportFn]);

    if (isLoadingLlamadas) {
        return <div className="ro-state">Cargando reporte...</div>;
    }

    if (!reporteOperacionData) {
        return (
            <div className="ro-state">
                Seleccione área, línea y fecha, luego presione «Ver Reporte»
            </div>
        );
    }

    return (
        <div className="ro-container">

            {/* Info header */}
            {(selectedArea || selectedLinea || fechaNominal) && (
                <div className="ro-info-header">
                    {selectedArea  && <span className="ro-info-chip">{selectedArea.nombre}</span>}
                    {selectedLinea && <span className="ro-info-chip">{selectedLinea.nombre}</span>}
                    {fechaNominal  && <span className="ro-info-chip">{String(fechaNominal).slice(0, 10)}</span>}
                </div>
            )}

            {/* Row 1: Producción + Rendimiento side by side */}
            <div className="ro-prod-rend-row">
                <SectionTable cols={COLS_PROD}  rows={allRows} sectionLabel="Producción"  />
                <SectionTable cols={COLS_REND}  rows={allRows} sectionLabel="Rendimiento" />
            </div>

            {/* Row 2: Llamadas full width */}
            <SectionTable cols={COLS_LLAMADAS} rows={allRows} sectionLabel="Llamadas de mantenimiento" />

            {/* Row 3: MTBF/MTTR + Gauges */}
            <div className="ro-bottom">
                <div className="ro-kpi-section">
                    <div className="ro-kpi-card">
                        <span className="ro-kpi-label">MTBF</span>
                        <span className="ro-kpi-value">
                            {mtbf != null ? `${parseFloat(mtbf).toFixed(1)} min` : '-'}
                        </span>
                    </div>
                    <div className="ro-kpi-card">
                        <span className="ro-kpi-label">MTTR</span>
                        <span className="ro-kpi-value">
                            {mttr != null ? `${parseFloat(mttr).toFixed(1)} min` : '-'}
                        </span>
                    </div>
                </div>
                <div className="ro-gauges-section">
                    <GaugeCard label="Disponibilidad" value={gaugeSource?.disponibilidad} />
                    <GaugeCard label="Desempeño"      value={gaugeSource?.desempeno}      />
                    <GaugeCard label="Calidad"         value={gaugeSource?.calidad}         />
                    <GaugeCard label="OEE"             value={gaugeSource?.oee}             />
                </div>
            </div>

        </div>
    );
}
